import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest, finalize } from 'rxjs';
import { PolicyModel, PolicyToUpdateModel } from 'src/app/models/api/governance/policy.model';
import { MatDialog } from '@angular/material/dialog';
import { AddDomainDialogComponent } from '../add-domain-dialog/add-domain-dialog.component';
import { IntegrationModel } from 'src/app/models/api';
import _ from 'lodash';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { InactiveIntegrationDialogComponent } from '../inactive-integration-dialog/inactive-integration-dialog.component';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { AccountSettingsHttpService } from 'src/app/services/httpServices/governance/accountSettingsHttpService/account-settings-http.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { IDomainsViewModel } from 'src/app/models/viewModels/domainsViewModel';
import { IWarningDialogInput, WarningDialogComponent } from '../warning-dialog/warning-dialog.component';
import { EffectivePolicySettingEnum } from 'src/app/services/infra/store/model/effectivePolicySetting.model';

@Component({
  selector: 'app-trusted-domains',
  templateUrl: './trusted-domains.component.html',
  styleUrls: ['./trusted-domains.component.scss'],
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        position: -webkit-sticky;
        position: sticky;
        top: 0px;
      }

      @media screen and (max-width: 64em) {
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
          top: 0px;
        }
      }
    `,
  ],
})
export class TrustedDomainsComponent implements OnInit, OnDestroy {
  @Input() public policyModel: PolicyModel;
  @Input() public integrationModel: IntegrationModel;
  @Input() public editable = true;
  @Input() public tableNotFoundText = 'No results found';
  @Input() public tableNotFoundIcon = 'assets/images/common/no-results-found.svg';
  @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  public EffectivePolicySettingEnum = EffectivePolicySettingEnum;

  // domains list
  public allowedDomainsStartState: string[];
  public domainsForSearchBox: string[] = [];

  // loading behavior subject
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public changesWereDone = false;

  public domainsCurrentPage$: BehaviorSubject<IDomainsViewModel[]> = new BehaviorSubject<IDomainsViewModel[]>([]);
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();

  private readonly _accountSettingsSubscription$?: Subscription;
  private readonly _policyConfigUpdateSubscription$?: Subscription;
  private _policyEntitySubscription$?: Subscription;

  private _domainsViewModel: IDomainsViewModel[] = [];

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _accountSettingsHttpService: AccountSettingsHttpService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {
    // in order to keep the scope of the function for this. inspiration: https://stackoverflow.com/questions/38245450/angular2-components-this-is-undefined-when-executing-callback-function
    this.deleteDomains = this.deleteDomains.bind(this);
  }

  ngOnInit(): void {
    this.allowedDomainsStartState = this.policyModel.config.allowedDomains.slice();
    this._domainsViewModel = this.allowedDomainsStartState.map((domain) => this._transformToDomainViewModel(domain));
    this._updateDomainsViewModel();
  }

  ngOnDestroy() {
    this._accountSettingsSubscription$?.unsubscribe();
    this._policyConfigUpdateSubscription$?.unsubscribe();
    this._policyEntitySubscription$?.unsubscribe();
  }

  public addDomainClicked() {
    const dialogRef = this._matDialog.open(AddDomainDialogComponent, {
      data: this.policyModel,
      disableClose: false,
      autoFocus: false,
      panelClass: 'dynamic-size-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((domains: string[]) => {
      if (domains) {
        this._domainsViewModel.push(...domains.map((domain) => this._transformToDomainViewModel(domain)));
        this._updateDomainsViewModel();
        this.changesWereDone = true;
        this.notifyChanges.emit(this.changesWereDone);
      }
    });
  }

  public resetForm() {
    this._domainsViewModel = this.allowedDomainsStartState.map((domain) => this._transformToDomainViewModel(domain));
    this._updateDomainsViewModel();
    this.resetSearchBoxBehavioralSubject$.next(this.allowedDomainsStartState);
    this.changesWereDone = false;

    this.notifyChanges.emit(this.changesWereDone);
  }

  public deleteDomains(domains: string[]): Observable<string> {
    const domainsAfterDeletion = this.allowedDomainsStartState.filter((domain) => !domains.includes(domain));
    return this._policiesHttpService.updatePolicy(
      this.integrationModel.id,
      this.policyModel.id,
      this._generateUpdatePolicyModel(domainsAfterDeletion)
    );
  }

  public domainSearched(domains: string[]) {
    const domainsViewModel = this._domainsViewModel
      .map((domainViewModel) => this._transformToDomainViewModel(domainViewModel.displayName))
      .filter((domainViewModel) => domains.includes(domainViewModel.displayName));
    this.domainsCurrentPage$.next(domainsViewModel);
  }

  public updatePolicyConfig() {
    if (this.integrationModel.connectionStatus.code == StatusCodeEnum.off) {
      const dialogRef = this._matDialog.open(InactiveIntegrationDialogComponent, {
        data: this.integrationModel,
        disableClose: false,
        panelClass: 'dynamic-size-popup-dialog-container',
      });
      dialogRef.afterClosed().subscribe(() => {
        this._updatePolicyConfig();
      });
    } else {
      this._updatePolicyConfig();
    }
  }

  public cancelSettings() {
    this.goBack.emit();
  }

  public removeDomainClicked(domainToRemove: IDomainsViewModel): void {
    const dialogRef = this._matDialog.open(WarningDialogComponent, {
      data: {
        elementsIds: [domainToRemove.rowId],
        elementsNames: [domainToRemove.displayName],
        title: 'policyPage.config.trustedDomainsContent.deleteDomain',
        notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
        actionCallback: this.deleteDomains,
      } as IWarningDialogInput,
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((domainDeleted?: boolean) => {
      if (domainDeleted) {
        const index = this._domainsViewModel.indexOf(domainToRemove);
        if (index > -1) this._domainsViewModel.splice(index, 1);
        // remove from policy model
        const domainIndex = this.policyModel.config.allowedDomains.indexOf(domainToRemove.rowId);
        if (domainIndex > -1) this.policyModel.config.allowedDomains.splice(domainIndex, 1);
        this._updateDomainsViewModel();
        this.policyModel.config.allowedDomains = this._domainsViewModel.map((domain) => domain.rowId);
        this.policyUpdated.emit(this.policyModel);
      }
      this.resetSearchBoxBehavioralSubject$.next(this.allowedDomainsStartState);
    });
  }

  private _updateDomainsViewModel() {
    this.domainsCurrentPage$.next(this._domainsViewModel);
    this.domainsForSearchBox = this._domainsViewModel.map((domain) => domain.displayName);
  }

  private _transformToDomainViewModel(domain: string): IDomainsViewModel {
    return {
      rowId: domain,
      displayName: domain,
      clickable: false,
    };
  }

  private _updatePolicyConfig() {
    this.isLoading$.next(true);
    const domains = this._domainsViewModel.map((domainViewModel) => domainViewModel.rowId);

    combineLatest({
      accountSettingsUpdate: this._accountSettingsHttpService.addDomains(domains),
      policyUpdate: this._policiesHttpService.updatePolicy(
        this.integrationModel.id,
        this.policyModel.id,
        this._generateUpdatePolicyModel(domains)
      ),
    })
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe({
        next: () => {
          this._notificationService.success('Policy updated successfully');
          this._getPolicyUpdates();
        },
        error: (error) => {
          this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private _getPolicyUpdates() {
    this._policyEntitySubscription$ = this._policiesHttpService
      .getPolicy(this.integrationModel.id, this.policyModel.id)
      .subscribe((policy) => {
        this.policyModel = policy;
        this.allowedDomainsStartState = this.policyModel.config.allowedDomains.slice();
        this._domainsViewModel = this.allowedDomainsStartState.map((domain) =>
          this._transformToDomainViewModel(domain)
        );
        this._updateDomainsViewModel();
        this.changesWereDone = false;

        this.notifyChanges.emit(this.changesWereDone);
        this.policyUpdated.emit(this.policyModel);
      });
  }

  private _generateUpdatePolicyModel(domains?: string[]): PolicyToUpdateModel {
    const config = _.cloneDeep(this.policyModel.config);
    config.allowedDomains = domains ? domains : this.allowedDomainsStartState;
    return {
      name: this.policyModel.name,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : this.policyModel.type,
      config: config,
    } as PolicyToUpdateModel;
  }
}
